import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
  Button,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import DataTable from "react-data-table-component";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { baseUrl, uploadFile, mediaUrl } from "../../../Utils/FileUpload";
import { Language } from "../../../Types/Tables.type";

import {
  Submit,
  EditLanguageTitle,
  LanguageTitle,
} from "../../../Utils/Constants";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useNavigate, useLocation } from "react-router-dom";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  language: string;
  icon: FileList | null;
}
interface ErrorResponse {
  message: string;
}
export default function EditLanguage() {
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      language: "",
      icon: null,
    },
  });
  const [existingFile, setExistingFile] = useState<string>("");
  const location = useLocation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Language[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [disabledDelete, setDisabledDelete] = useState<string | null>(null);
  const [disableAllDelete, setDisableAllDelete] = useState(false);
  const token = localStorage.getItem("token");
  const [disabledEnable, setDisabledEnable] = useState<string | null>(null);
  const [disableAllEnable, setDisableAllEnable] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseUrl}language/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data: Language[] = response.data;
        setFilteredItems(data);

        if (location.state?.id) {
          // make sure id exists
          const result = await axios.post(
            `${baseUrl}language/viewbyid`,
            { id: location.state.id },
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setValue("language", result.data.language);
          setValue("icon", result.data.icon);
          setExistingFile(result.data.icon);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.state?.id, setValue, refresh]);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      let filename = existingFile;

      // Check if a new file is provided
      if (data.icon && data.icon instanceof FileList && data.icon.length > 0) {
        const file = data.icon?.[0];
        filename = await uploadFile(file);
        setExistingFile(filename);
      }

      const editdata = {
        language: data.language,
        icon: filename || existingFile,
        id: location.state.id,
      };
      // console.log(editdata, "editdata");
      const token = localStorage.getItem("token");
      const response = await axios.post(`${baseUrl}language/update`, editdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        SuccessToast("Data updated successfully", "/view_language");
      } else {
        ErrorToast("Failed to update");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorResponse = axiosError.response?.data as ErrorResponse;
      const errorMessage =
        errorResponse?.message || "An unexpected error occurred";
      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const filteredData = filteredItems.filter((item) => {
    return (
      (item.language &&
        item.language.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.icon && item.icon.toLowerCase().includes(filterText.toLowerCase()))
    );
  });

  const handleEdit = (id: string) => {
    // console.log(id,"id")
    navigate("/edit_language", { state: { id: id } });
  };

  const handleDelete = (id: string) => {
    setDisabledDelete(id);
    setDisableAllDelete(true);
    const confirmToast = () =>
      toast.warn(
        <div>
          <div>Are you sure you want to delete this language?</div>
          <div className="d-flex justify-content-end mt-2">
            <Button color="danger" onClick={() => confirmDelete(id)}>
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledDelete(null);
                setDisableAllDelete(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisableAllDelete(false);
            setDisabledDelete(null);
          },
        }
      );

    const confirmDelete = async (id: string) => {
      setDisabledDelete(null);
      setDisableAllDelete(false);
      toast.dismiss();
      try {
        await axios.post(
          `${baseUrl}language/delete`,
          { id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`Language Deleted Successfully`, {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
        setFilteredItems((prevItems) =>
          prevItems.filter((item) => item.id !== id)
        );
      } catch (error) {
        console.error("Error deleting language:", error);
        toast.error("An error occurred while deleting the language", {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
      }
    };

    confirmToast();
  };

  const toggleEnable = (id: string, currentStatus: number) => {
    setDisabledEnable(id); // reuse this state if needed
    setDisableAllEnable(true);

    const confirmToggle = () => {
      toast.warn(
        <div>
          <div>
            Are you sure you want to{" "}
            <strong>{currentStatus === 1 ? "disable" : "enable"}</strong> this
            language?
          </div>
          <div className="d-flex justify-content-end mt-2">
            <Button
              color={currentStatus === 1 ? "danger" : "success"}
              onClick={() => confirmToggleEnable(id, currentStatus)}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledEnable(null);
                setDisableAllEnable(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisabledEnable(null);
            setDisableAllEnable(false);
          },
        }
      );
    };

    const confirmToggleEnable = async (id: string, currentStatus: number) => {
      setDisabledEnable(null);
      setDisableAllEnable(false);
      toast.dismiss();

      try {
        const newStatus = currentStatus === 1 ? 0 : 1;

        await axios.post(
          `${baseUrl}language/toggle_active`,
          { id, is_active: newStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success(
          `Language ${newStatus === 1 ? "enabled" : "disabled"} successfully!`,
          {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
            onClose: () => {
              setRefresh((prev) => prev + 1);
            },
          }
        );
      } catch (error) {
        console.error("Error toggling language:", error);
        toast.error("Error updating enable status", {
          position: "top-right",
          autoClose: 1000,
        });
      }
    };

    confirmToggle();
  };
  // Define columns with actions
  const languageTableColumns = [
    {
      name: "Icon",
      cell: (row: Language) => (
        <img
          src={`${mediaUrl}/${row.icon}`}
          alt={row.icon}
          width={25}
          height={25}
          style={{
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      ),
      // sortable: true,
      width: "25%",
    },
    {
      name: "Language",
      selector: (row: Language) => row.language,
      sortable: true,
      width: "25%",
    },

    {
      name: "Status",
      cell: (row: Language) => (
        <span
          className={`badge rounded-pill ${
            row.is_active === 1 ? "bg-success" : "bg-danger"
          }`}
        >
          {row.is_active === 1 ? "Enable" : "Disable"}
        </span>
      ),
      // sortable: true,
      width: "25%",
    },
    {
      name: "Actions",
      cell: (row: Language) => (
        <div className="d-flex justify-content-between">
          <Button
            color={row.is_active ? "success" : "secondary"}
            size="sm"
            onClick={() => toggleEnable(row.id, row.is_active)}
            disabled={disabledEnable === row.id || disableAllEnable}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={row.is_active ? "Disable" : "Enable"}
          >
            <i
              className={`fa ${row.is_active ? "fa-check-circle" : "fa-ban"}`}
            />
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={() => handleEdit(row.id)}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="icon-pencil-alt" />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
            disabled={disabledDelete === row.id || disableAllDelete}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="icon-trash" />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      // button: true,
      width: "25%",
    },
  ];

  // Show a loading spinner while data is being fetched
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }
  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={EditLanguageTitle}
        parent={LanguageTitle}
        title={EditLanguageTitle}
      />
      <Row>
        <Col xl={4} className="mb-lg-3">
          <Card>
            <CardBody className="custom-input form-validation">
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col xs={12}>
                    <Label htmlFor="language-name">Language</Label>
                    <Controller
                      name="language"
                      control={control}
                      rules={{
                        required: "Language is required",
                        maxLength: {
                          value: 25,
                          message:
                            "Language cannot be more than 25 characters.",
                        },
                        pattern: {
                          value: /^[A-Za-z\s\W_]+$/,
                          message:
                            "Only letters, spaces, and symbols are allowed. Numbers are not allowed.",
                        },
                        validate: (value) => {
                          if (/\d/.test(value)) {
                            return "Numbers are not allowed.";
                          }
                          if (value.trim() === "") {
                            return "Language cannot be empty or only spaces.";
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <Input
                            id="language-name"
                            type="text"
                            placeholder="Language"
                            {...field}
                            invalid={!!errors.language}
                            style={{
                              color: "var(--input-text-color)",
                              borderColor: errors.language ? "red" : "",
                            }}
                            autoComplete="off"
                            onKeyDown={handleKeyDown}
                          />
                          {errors.language && (
                            <FormFeedback>
                              {errors.language.message}
                            </FormFeedback>
                          )}
                        </>
                      )}
                    />
                  </Col>

                  <Col xs={12}>
                    <Label htmlFor="formFile">Icon</Label>
                    <Controller
                      name="icon"
                      control={control}
                      rules={{ required: "Icon is required" }}
                      render={({ field: { onChange, onBlur, ref } }) => (
                        <>
                          <Input
                            id="formImage"
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                onChange(e.target.files);
                                // Update the image preview immediately
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setExistingFile(reader.result as string);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            onBlur={onBlur}
                            innerRef={ref}
                            onKeyDown={handleKeyDown}
                          />
                          {existingFile && (
                            <img
                              src={
                                existingFile.startsWith("data:")
                                  ? existingFile
                                  : `${mediaUrl}/${existingFile}`
                              }
                              alt="Preview"
                              width="70"
                              height="70"
                              style={{ marginTop: "5px", borderRadius: "10px" }}
                            />
                          )}
                        </>
                      )}
                    />
                  </Col>

                  <Col xs={12} className="mt-2">
                    <Btn color="primary" disabled={isButtonDisabled}>
                      {Submit}
                    </Btn>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
        <Col xl={8} className="mb-lg-3">
          <Card>
            <CardBody>
              <div className="mb-3">
                <FilterComponent
                  onFilter={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFilterText(e.target.value)
                  }
                  filterText={filterText}
                />
              </div>
              <div className="table-responsive">
                <DataTable
                  data={filteredData}
                  columns={languageTableColumns}
                  pagination
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
